import {
    DeviceIdService,
    StateService,
    TrafficSourceService,
    UTMTrackerService,
} from '@pulse/browser-shared';
import { Experiment, PulseStorage, SettingsConfigurations } from '@pulse/common';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { PulseCookie, PulseCookieRecordPosition } from '../models';
import { ExperimentDebugService } from './experiment-debug.service';
import { ExperimentsService } from './experiments.service';
import { IsTestService } from './is-test.service';
import { SettingsHelper } from './settings/settings.helper';
import { UserRoleService } from './user-role.service';

export class CookieSyncService {
    private syncCookieSubject = new Subject<
        Partial<Record<keyof typeof PulseCookieRecordPosition, string>>
    >();
    private pendingUpdates: Partial<Record<keyof typeof PulseCookieRecordPosition, string>> = {};

    constructor(
        protected storage: PulseStorage, // Persistent Storage
        protected readonly cookieStorage: PulseStorage,
        protected config: SettingsConfigurations,
        protected deviceIdService: DeviceIdService,
        protected experimentsService: ExperimentsService,
        protected utmService: UTMTrackerService,
        protected trafficSourceService: TrafficSourceService,
        protected userRoleService: UserRoleService,
        protected settingsHelper: SettingsHelper,
        protected stateService: StateService,
        protected isTestService: IsTestService,
        protected experimentDebugService: ExperimentDebugService,
    ) {
        this.syncCookieSubject.pipe(debounceTime(50)).subscribe(() => {
            this.executeSyncCookie();
            this.pendingUpdates = {};
        });

        const cookie = this.parseCookie(
            this.cookieStorage.get(this.config.keyPulseSyncCookie) || '',
        );

        if (cookie) {
            this.updatePulseStateFromCookie(cookie);
        }
    }

    public startService() {
        this.registerStateChangeListeners();
    }

    private updatePulseStateFromCookie(cookie: PulseCookie): void {
        if (cookie[PulseCookieRecordPosition.DEVICE_ID]) {
            this.deviceIdService.setDeviceId(cookie[PulseCookieRecordPosition.DEVICE_ID]);
        }

        if (cookie[PulseCookieRecordPosition.EXPERIMENTS]) {
            const experiments = this.parseExperimentsFromCookie(
                cookie[PulseCookieRecordPosition.EXPERIMENTS],
            );

            if (experiments.length > 0) {
                this.experimentsService.cacheResponse({
                    experiments,
                    emptyForTags: [],
                    attributesHash: '',
                });
            }
        }
    }

    private parseExperimentsFromCookie(experimentsString: string): Experiment[] {
        if (!experimentsString || !experimentsString.trim()) {
            return [];
        }

        try {
            const decodedString = decodeURIComponent(experimentsString);
            const experimentPairs = decodedString.split(',');

            return experimentPairs.map((pair): Experiment => {
                const [name, variant] = pair.split(':');

                return {
                    name: name.trim(),
                    variant: variant.trim(),
                    trackable: 1,
                    settings: [],
                    participation_session: null,
                    participation_date: null,
                };
            });
        } catch (error) {
            console.error('Failed to parse experiments from cookie:', error);

            return [];
        }
    }

    private serializeAttributesForCookie(attributes: Record<string, string>): string {
        if (!attributes || Object.keys(attributes).length === 0) {
            return '';
        }

        const params = new URLSearchParams();
        Object.entries(attributes).forEach(([key, value]) => {
            if (value) {
                params.set(key, value);
            }
        });

        return params.toString();
    }

    private setSyncCookie(
        updates: Partial<Record<keyof typeof PulseCookieRecordPosition, string>>,
    ): void {
        Object.assign(this.pendingUpdates, updates);
        this.syncCookieSubject.next(updates);
    }

    private executeSyncCookie(): void {
        const deviceId = this.deviceIdService.getDeviceId();
        const experiments = this.experimentsService.getExperimentsString();
        const trafficSource = this.trafficSourceService.getLastTrafficSource();
        const utmParams = this.utmService.getUTMParams();
        const userRole = this.userRoleService.getValue();
        const isTest = this.isTestService.getValue();
        const attributes = this.config.attributes;
        let attributesStr = '';

        if (attributes) {
            const serialized = this.serializeAttributesForCookie(attributes);
            attributesStr = serialized ? encodeURIComponent(serialized) : '';
        }

        const debugExperiment = this.experimentDebugService.getDebugExperiment();
        const debugExperimentForCookie = debugExperiment
            ? `${debugExperiment.id}:${debugExperiment.variant}`
            : '';
        const cookies = [
            deviceId,
            this.storage.get('userId') || '',
            isTest || '',
            experiments || '',
            debugExperimentForCookie,
            trafficSource?.referrer?.url || '',
            trafficSource?.referrer?.internal ? 'true' : '',
            utmParams?.utm_campaign || '',
            utmParams?.utm_source || '',
            utmParams?.utm_medium || '',
            trafficSource?.clickId?.value || '',
            trafficSource?.clickId?.source || '',
            this.storage.get('tierId') || '',
            this.storage.get('deviceLastTierId') || '',
            userRole || '',
            attributesStr,
        ];

        Object.entries(this.pendingUpdates).forEach(([key, value]) => {
            const index = PulseCookieRecordPosition[key as keyof typeof PulseCookieRecordPosition];

            if (typeof index === 'number' && value !== undefined) {
                cookies[index] = value;
            }
        });

        this.cookieStorage.set(this.config.keyPulseSyncCookie, cookies.join('|'));
    }

    private registerStateChangeListeners(): void {
        this.deviceIdService.getDeviceId$().subscribe((deviceId) => {
            this.setSyncCookie({ DEVICE_ID: deviceId });
        });

        this.stateService
            .getState$()
            .pipe(
                map((state) => ({
                    user_id: String(state?.['user_id'] || ''),
                    tier_id: String(state?.['tier_id'] || ''),
                    device_last_tier_id: String(state?.['device_last_tier_id'] || ''),
                })),
                distinctUntilChanged(
                    (prev, curr) =>
                        prev.user_id === curr.user_id &&
                        prev.tier_id === curr.tier_id &&
                        prev.device_last_tier_id === curr.device_last_tier_id,
                ),
            )
            .subscribe(({ user_id, tier_id, device_last_tier_id }) => {
                this.setSyncCookie({
                    USER_ID: user_id,
                    TIER_ID: tier_id,
                    DEVICE_LAST_TIER_ID: device_last_tier_id,
                });
            });

        this.isTestService.getValue$().subscribe((isTest) => {
            this.setSyncCookie({ IS_TEST: isTest || '' });
        });

        this.experimentsService.getExperimentsString$().subscribe((experiments) => {
            this.setSyncCookie({ EXPERIMENTS: experiments || '' });
        });

        this.experimentDebugService.getDebugExperiment$().subscribe((debugExperiment) => {
            const debugExperimentString = debugExperiment
                ? `${debugExperiment.id}:${debugExperiment.variant}`
                : '';
            this.setSyncCookie({ DEBUG_EXPERIMENTS: debugExperimentString });
        });

        this.trafficSourceService.getLastTrafficSource$().subscribe((trafficSource) => {
            this.setSyncCookie({
                REFERRER: trafficSource?.referrer?.url || '',
                INTERNAL_REFERRER: trafficSource?.referrer?.internal ? 'true' : '',
                CLICK_ID: trafficSource?.clickId?.value || '',
                CLICK_ID_SRC: trafficSource?.clickId?.source || '',
                UTM_CAMPAIGN: trafficSource?.utmParams?.utm_campaign || '',
                UTM_SOURCE: trafficSource?.utmParams?.utm_source || '',
                UTM_MEDIUM: trafficSource?.utmParams?.utm_medium || '',
            });
        });

        this.userRoleService.getValue$().subscribe((userRole) => {
            this.setSyncCookie({ USER_ROLE: userRole || '' });
        });
    }

    private parseCookie(cookie: string): PulseCookie | null {
        if (!cookie) {
            return null;
        }

        const parts = cookie.split('|');

        if (parts.length === 16) {
            return parts.slice(0, 16) as PulseCookie;
        }

        return null;
    }
}
